import React, {useState, useCallback, useReducer, useRef} from 'react';
import './App.css';
import {getDataFromUrl, setDataToUrl} from './storage';
import {StateT, ProgressionT, TextT, BarT, ColorSetT, StateActionT} from './types';
import {useMouseUpListener} from './use_mouse_up_listener';
console.clear();

const GRID_INTERVAL = 20;
const BAR_HORIZONTAL_INTERVAL = GRID_INTERVAL;
const BAR_WIDTH = BAR_HORIZONTAL_INTERVAL - 1;
const TEXT_HEIGHT = 23;
const MAX_AMOUNT = 9999999; // Browser can't correctly render bars above a certain size
const TEXT_CHAR_WIDTH = 12;

const COLOR_SETS: {[color: string]: ColorSetT} = {
    orange: {newGrowth: '#fc0', oldGrowth: '#f90', principal: '#f70'},
    green: {newGrowth: '#0c0', oldGrowth: '#090', principal: '#070'},
    blue: {newGrowth: '#66f', oldGrowth: '#22a', principal: '#007'},
};

function getNextId(collection: Array<any>): number {
    return collection.length;
}

function getNextColor(color: string): string {
    const colors = Object.keys(COLOR_SETS);
    const colorIndex = colors.indexOf(color);
    return colors[(colorIndex + 1) % colors.length];
}

function getCompoundAmount(principal: number, growthRate: number, period: number): number {
    return principal * Math.pow(1 + growthRate, period);
}

function getGrowthRate(principal: number, amount: number, period: number): number {
    return Math.pow(amount / principal, 1 / period) - 1;
}

function range(end: number): Array<number> {
    return Array.from(Array(end).keys());
}

function getTextWidth(str: string): number {
    return str.length * TEXT_CHAR_WIDTH;
}

function getStarterProgression(state: StateT): ProgressionT {
    return {
        id: getNextId(state.progressions),
        principal: 100,
        growthRate: 0.05,
        periods: 10,
        x: 100,
        y: 600,
        color: Object.keys(COLOR_SETS)[0],
        zIndex: getNextZIndex(state),
    };
}

function getInitialState(): StateT {
    const state: StateT = {
        progressions: [],
        texts: [],
    };

    state.progressions.push({
        id: getNextId(state.progressions),
        principal: 100,
        growthRate: 0.1,
        periods: 10,
        x: 200,
        y: 500,
        color: Object.keys(COLOR_SETS)[0],
        zIndex: getNextZIndex(state),
    });

    return state;
}

function getNextZIndex(state: StateT): number {
    return state.progressions.length + state.texts.length;
}

function DeleteButton({onDelete, shouldShow}: {onDelete: () => void; shouldShow: boolean}) {
    const onClick = useCallback(
        (e) => {
            e.stopPropagation();
            onDelete();
        },
        [onDelete],
    );

    return (
        <div
            style={{
                width: 30,
                height: 30,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {shouldShow && (
                <div
                    onClick={onClick}
                    style={{
                        fontSize: 20,
                        color: '#eee',
                        fontWeight: 800,
                        borderRadius: 20,
                        width: 20,
                        height: 20,
                        backgroundColor: '#666',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        cursor: 'pointer',
                    }}
                >
                    <div style={{height: 31, userSelect: 'none'}}>⨯</div>
                </div>
            )}
        </div>
    );
}

function App() {
    const loaded = getDataFromUrl() || getInitialState();

    const [state, dispatch] = useReducer((state: StateT, action: StateActionT): StateT => {
        let newState;
        switch (action.type) {
            case 'addProgression': {
                newState = {
                    ...state,
                    progressions: [
                        ...state.progressions,
                        {
                            ...getStarterProgression(state),
                            color:
                                state.progressions.length > 0
                                    ? getNextColor(
                                          state.progressions[state.progressions.length - 1].color,
                                      )
                                    : Object.keys(COLOR_SETS)[0],
                        },
                    ],
                };
                break;
            }
            case 'duplicateProgression': {
                newState = {
                    ...state,
                    progressions: [
                        ...state.progressions.map((progression: ProgressionT) => {
                            if (progression.id === action.data.id) {
                                return {
                                    ...progression,
                                    color: getNextColor(action.data.color),
                                    zIndex: getNextZIndex(state),
                                    x: progression.x + 10,
                                    y: progression.y + 10,
                                };
                            }

                            return progression;
                        }),
                        {
                            ...action.data,
                            id: getNextId(state.progressions),
                        },
                    ],
                };
                break;
            }
            case 'deleteProgression': {
                newState = {
                    ...state,
                    progressions: state.progressions.filter((progression: ProgressionT) => {
                        return progression.id !== action.id;
                    }),
                };
                break;
            }
            case 'moveProgression': {
                newState = {
                    ...state,
                    progressions: state.progressions.map((item: ProgressionT) =>
                        item.id === action.id ? {...item, x: action.x, y: action.y} : item,
                    ),
                };
                break;
            }
            case 'setGrowthRate': {
                newState = {
                    ...state,
                    progressions: state.progressions.map((item: ProgressionT) =>
                        item.id === action.id ? {...item, growthRate: action.growthRate} : item,
                    ),
                };
                break;
            }
            case 'setPrincipal': {
                newState = {
                    ...state,
                    progressions: state.progressions.map((item: ProgressionT) =>
                        item.id === action.id ? {...item, principal: action.principal} : item,
                    ),
                };
                break;
            }
            case 'setPeriods': {
                newState = {
                    ...state,
                    progressions: state.progressions.map((item: ProgressionT) =>
                        item.id === action.id ? {...item, periods: action.periods} : item,
                    ),
                };
                break;
            }
            case 'addText': {
                newState = {
                    ...state,
                    texts: [
                        ...state.texts,
                        {
                            id: getNextId(state.texts),
                            x: 100,
                            y: 100,
                            text: action.text,
                            zIndex: getNextZIndex(state),
                        },
                    ],
                };
                break;
            }
            case 'duplicateText': {
                newState = {
                    ...state,
                    texts: [
                        ...state.texts.map((text: TextT) => {
                            if (text.id === action.data.id) {
                                return {
                                    ...text,
                                    zIndex: getNextZIndex(state),
                                    x: text.x + 10,
                                    y: text.y + 10,
                                };
                            }

                            return text;
                        }),
                        {
                            ...action.data,
                            id: getNextId(state.texts),
                        },
                    ],
                };
                break;
            }
            case 'deleteText': {
                newState = {
                    ...state,
                    texts: state.texts.filter((text: TextT) => {
                        return text.id !== action.id;
                    }),
                };
                break;
            }
            case 'moveText': {
                newState = {
                    ...state,
                    texts: state.texts.map((item: TextT) =>
                        item.id === action.id ? {...item, x: action.x, y: action.y} : item,
                    ),
                };
                break;
            }
            case 'setText': {
                newState = {
                    ...state,
                    texts: state.texts.map((text: TextT) =>
                        text.id === action.id ? {...text, text: action.text} : text,
                    ),
                };
                break;
            }

            default:
                throw new Error('Unknown action type');
        }

        setDataToUrl(JSON.stringify(newState));
        return newState;
    }, loaded);

    return (
        <div style={{position: 'absolute', top: 0, right: 0, bottom: 0, left: 0}}>
            <button
                style={{position: 'absolute', top: 10, left: 10, userSelect: 'none'}}
                onClick={() => {
                    dispatch({type: 'addProgression'});
                }}
            >
                Add exponential chart
            </button>

            <button
                style={{position: 'absolute', top: 10, left: 163, userSelect: 'none'}}
                onClick={() => {
                    dispatch({type: 'addText', text: `Text ${state.texts.length + 1}`});
                }}
            >
                Add text
            </button>

            <button
                style={{position: 'absolute', top: 10, right: 10, userSelect: 'none'}}
                onClick={() => {
                    window.open('/', '_blank noreferrer');
                }}
            >
                Open a new canvas
            </button>

            {state.progressions.map((progression: ProgressionT, i: number) => {
                return <Progression key={i} progression={progression} dispatch={dispatch} />;
            })}

            {state.texts.map((text: TextT, i: number) => {
                return <Text key={i} text={text} dispatch={dispatch} />;
            })}
        </div>
    );
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
function Text({text, dispatch}: {text: TextT; dispatch: (action: StateActionT) => void}) {
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [draggingOffset, setDraggingOffset] = useState<{x: number; y: number} | null>(null);
    const [isHovering, setIsHovering] = useState<boolean>(false);
    const [textString, setTextString] = useState<string>(text.text);

    const onStartDragging = useCallback(
        (e) => {
            if (e.button !== 0 || isEditing) {
                return;
            }

            if (e.altKey) {
                dispatch({type: 'duplicateText', data: text});
            }

            setIsDragging(true);
            setDraggingOffset({x: e.pageX - text.x, y: e.pageY - text.y});
        },
        [setIsDragging, setDraggingOffset, text, isEditing, dispatch],
    );

    const onMouseMove = useCallback(
        (e) => {
            if (!isDragging || !draggingOffset) {
                return;
            }

            dispatch({
                type: 'moveText',
                id: text.id,
                x: Math.ceil((e.pageX - draggingOffset.x) / GRID_INTERVAL) * GRID_INTERVAL,
                y: Math.ceil((e.pageY - draggingOffset.y) / GRID_INTERVAL) * GRID_INTERVAL,
            });
        },
        [isDragging, draggingOffset, text, dispatch],
    );

    useMouseUpListener(
        useCallback(() => {
            setIsDragging(false);
        }, [setIsDragging]),
    );

    const onStartEditingText = useCallback(
        (e) => {
            setIsEditing(true);
        },
        [setIsEditing],
    );

    const onChange = useCallback(
        (e) => {
            setTextString(e.target.value);
        },
        [setTextString],
    );

    const onStopEditing = useCallback(() => {
        setIsEditing(false);

        if (textString.trim() === '') {
            return;
        }

        dispatch({
            type: 'setText',
            id: text.id,
            text: textString,
        });
    }, [setIsEditing, text, textString, dispatch]);

    const onKeyDown = useCallback(
        (e) => {
            if (e.key === 'Enter') {
                onStopEditing();
            }
        },
        [onStopEditing],
    );

    const onMouseEnter = useCallback(() => {
        setIsHovering(true);
    }, [setIsHovering]);

    const onMouseLeave = useCallback(() => {
        setIsHovering(false);
    }, [setIsHovering]);

    const onDelete = useCallback(() => {
        dispatch({type: 'deleteText', id: text.id});
    }, [text, dispatch]);

    const top = isEditing ? text.y - 2 : text.y - (isDragging ? 100 : 0);
    const left = isEditing ? text.x - 3 : text.x - (isDragging ? 100 : 0);

    return (
        <div
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            style={{
                position: 'absolute',
                top,
                left,
                width: 300,
                userSelect: 'none',
            }}
        >
            <div
                onMouseDown={onStartDragging}
                onMouseMove={onMouseMove}
                onDoubleClick={onStartEditingText}
                style={{
                    zIndex: isDragging ? 10 : 0, // So other objects don't capture mouse moves when dragged over
                    cursor: isDragging ? 'grabbing' : 'grab',
                    padding: isDragging ? 100 : 0,
                    width: 300,
                    fontFamily: 'courier',
                    opacity: 0.9,
                }}
            >
                {isEditing ? (
                    <textarea
                        style={{
                            fontFamily: 'courier',
                            fontSize: 15,
                            letterSpacing: 0.6,
                            lineHeight: 1.2,
                        }}
                        rows={4}
                        cols={32}
                        value={textString}
                        onChange={onChange}
                        onKeyDown={onKeyDown}
                        onBlur={onStopEditing}
                        autoFocus
                    ></textarea>
                ) : (
                    text.text
                )}
            </div>

            <div style={{position: 'absolute', top: -24, left: -24}}>
                <DeleteButton shouldShow={isHovering && !isDragging} onDelete={onDelete} />
            </div>
        </div>
    );
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
function Progression({
    progression,
    dispatch,
}: {
    progression: ProgressionT;
    dispatch: (action: StateActionT) => void;
}) {
    const [isDraggingProgression, setIsDraggingProgression] = useState<boolean>(false);
    const [isDraggingAnything, setIsDraggingAnything] = useState<boolean>(false);
    const [draggingOffset, setDraggingOffset] = useState<{x: number; y: number} | null>(null);
    const [isHovering, setIsHovering] = useState<boolean>(false);

    const bars = range(progression.periods).map(
        (i): BarT => {
            const period = i + 1;
            const principal = progression.principal;
            const amount = Math.min(
                getCompoundAmount(progression.principal, progression.growthRate, period),
                MAX_AMOUNT,
            );
            const growth = amount - principal;
            const oldGrowth =
                Math.min(
                    getCompoundAmount(progression.principal, progression.growthRate, period - 1),
                    MAX_AMOUNT,
                ) - principal;
            const newGrowth = growth - oldGrowth;

            return {
                principal,
                amount,
                growth,
                newGrowth,
                oldGrowth,
                period,
            };
        },
    );

    const firstBar = bars[0];
    const lastBar = bars[bars.length - 1];

    if (!firstBar || !lastBar) {
        throw new Error('Must be at least one bar');
    }

    const addPeriodsLeft = lastBar.period * BAR_HORIZONTAL_INTERVAL;

    const onStartDragging = useCallback(
        (e) => {
            if (e.button !== 0) {
                return;
            }

            if (e.altKey) {
                dispatch({type: 'duplicateProgression', data: progression});
            }

            setIsDraggingProgression(true);
            setIsDraggingAnything(true);
            setDraggingOffset({x: e.pageX - progression.x, y: e.pageY - progression.y});
        },
        [setIsDraggingProgression, setDraggingOffset, progression, dispatch],
    );

    const onMouseMove = useCallback(
        (e) => {
            if (!isDraggingProgression || !draggingOffset) {
                return;
            }

            dispatch({
                type: 'moveProgression',
                id: progression.id,
                x: Math.ceil((e.pageX - draggingOffset.x) / GRID_INTERVAL) * GRID_INTERVAL,
                y: Math.ceil((e.pageY - draggingOffset.y) / GRID_INTERVAL) * GRID_INTERVAL,
            });
        },
        [isDraggingProgression, draggingOffset, progression, dispatch],
    );

    useMouseUpListener(
        useCallback(() => {
            setIsDraggingProgression(false);
            setIsDraggingAnything(false);
        }, [setIsDraggingProgression]),
    );

    const updateBarAmount = useCallback(
        (amountPageY: number, period: number) => {
            let newAmount = progression.y - amountPageY;

            // Don't allow amount to become negative. If negative,
            // set target amount to 0 and set growth rate based on that
            // so we get a clean, empty bar, rather than a smidge left.
            if (newAmount < 0) {
                newAmount = 0;
            }

            let newGrowthRate = getGrowthRate(progression.principal, newAmount, period);

            // Doesn't allow growthRate to become negative mainly because
            // showing the negative change doesn't work well with the model of
            // "the height of the bar equals the amount" visual language (because
            // we'd want to draw the negative growth which would look like part of the
            // amount).  Also because there's a bug with dragging the principal when
            // it's negative, but that's fixable.
            if (newGrowthRate < 0) {
                newGrowthRate = 0;
            }

            dispatch({
                type: 'setGrowthRate',
                id: progression.id,
                growthRate: newGrowthRate,
            });
        },
        [dispatch, progression],
    );

    const updateBarPrincipal = useCallback(
        (pageY: number) => {
            let newPrincipal = progression.y - pageY;

            // Don't allow principal to become negative. If negative,
            // set target principal to 0 so we get a clean, empty bar,
            // rather than a smidge left.
            if (newPrincipal < 0) {
                newPrincipal = 0;
            }

            dispatch({
                type: 'setPrincipal',
                id: progression.id,
                principal: newPrincipal,
            });
        },
        [dispatch, progression],
    );

    const updatePeriods = useCallback(
        (periodChange: number) => {
            const periods = progression.periods + periodChange;

            if (periods < 1) {
                return;
            }

            dispatch({
                type: 'setPeriods',
                id: progression.id,
                periods,
            });
        },
        [dispatch, progression],
    );

    const onMouseEnter = useCallback(() => {
        setIsHovering(true);
    }, [setIsHovering]);

    const onMouseLeave = useCallback(() => {
        setIsHovering(false);
    }, [setIsHovering]);

    const onDelete = useCallback(() => {
        dispatch({type: 'deleteProgression', id: progression.id});
    }, [progression, dispatch]);

    let zIndex;
    if (isDraggingProgression || isDraggingAnything) {
        zIndex = 10;
    } else {
        zIndex = progression.zIndex;
    }

    return (
        <div
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            style={{
                zIndex,
                cursor: isDraggingProgression ? 'grabbing' : 'grab',
                position: 'absolute',
                top: progression.y,
                left: progression.x,
            }}
        >
            <div onMouseDown={onStartDragging} onMouseMove={onMouseMove}>
                {/* Hack: Surrounds progression when dragging to avoid dropping drag if move quickly */}
                {isDraggingProgression && (
                    <div
                        style={{
                            position: 'absolute',
                            bottom: -50,
                            left: -50,
                            padding: 50,
                            width: progression.periods * BAR_HORIZONTAL_INTERVAL,
                            height: lastBar.amount,
                        }}
                    ></div>
                )}

                {bars.map((bar: BarT, i: number) => {
                    return (
                        <Bar
                            key={i}
                            bar={bar}
                            colorSet={COLOR_SETS[progression.color]}
                            updateBarPrincipal={updateBarPrincipal}
                            updateBarAmount={updateBarAmount}
                            isDraggingProgression={isDraggingProgression}
                            setIsDraggingAnything={setIsDraggingAnything}
                        />
                    );
                })}

                <FirstBarLabels
                    principal={progression.principal}
                    growthRate={progression.growthRate}
                    firstBar={firstBar}
                    color={progression.color}
                    updateBarAmount={updateBarAmount}
                    setIsDraggingAnything={setIsDraggingAnything}
                    isHoveringProgression={isHovering}
                />

                <AddPeriods
                    key="addPeriods"
                    height={lastBar.amount}
                    top={progression.y}
                    left={addPeriodsLeft}
                    setIsDraggingAnything={setIsDraggingAnything}
                    updatePeriods={updatePeriods}
                />

                <LastBarLabels
                    principal={progression.principal}
                    amount={lastBar.amount}
                    lastBar={lastBar}
                    color={progression.color}
                    updateBarAmount={updateBarAmount}
                    updateBarPrincipal={updateBarPrincipal}
                    setIsDraggingAnything={setIsDraggingAnything}
                    isHoveringProgression={isHovering}
                />

                <PeriodLabel
                    periods={progression.periods}
                    color={progression.color}
                    setIsDraggingAnything={setIsDraggingAnything}
                    updatePeriods={updatePeriods}
                    isHoveringProgression={isHovering}
                />
            </div>

            <div style={{position: 'absolute', bottom: -15, left: -15}}>
                <DeleteButton shouldShow={isHovering && !isDraggingAnything} onDelete={onDelete} />
            </div>
        </div>
    );
}

function useDragStartMouseOffsetFromHandleMiddleY(): [
    React.RefObject<HTMLDivElement>,
    (e: MouseEvent) => void,
    () => number,
] {
    const handleRef = useRef<HTMLDivElement>(null);
    const [dragStartOffsetFromHandleMiddleY, setDragStartOffsetFromHandleMiddleY] = useState<
        number | null
    >(null);

    const onStartDragging = useCallback(
        (e: MouseEvent) => {
            if (!handleRef || !handleRef.current) {
                throw new Error('Element should be defined');
            }

            const handleBoundingRect = handleRef.current.getBoundingClientRect();
            const distanceFromMouseToHandleMiddle =
                e.pageY - handleBoundingRect.top - handleBoundingRect.height / 2;
            setDragStartOffsetFromHandleMiddleY(distanceFromMouseToHandleMiddle);
        },
        [setDragStartOffsetFromHandleMiddleY],
    );

    const getDragStartOffsetFromHandleMiddleY = useCallback(() => {
        if (dragStartOffsetFromHandleMiddleY === null) {
            throw new Error('Should be defined');
        }

        return dragStartOffsetFromHandleMiddleY;
    }, [dragStartOffsetFromHandleMiddleY]);

    return [handleRef, onStartDragging, getDragStartOffsetFromHandleMiddleY];
}

function FirstBarLabels({
    principal,
    growthRate,
    firstBar,
    color,
    updateBarAmount,
    setIsDraggingAnything,
    isHoveringProgression,
}: {
    principal: number;
    growthRate: number;
    firstBar: BarT;
    color: string;
    updateBarAmount: (pageY: number, period: number) => void;
    setIsDraggingAnything: (isDragging: boolean) => void;
    isHoveringProgression: boolean;
}) {
    const {oldGrowth, newGrowth} = firstBar;
    const [isDraggingAmount, setIsDraggingAmount] = useState<boolean>(false);
    const [
        handleRef,
        onStartDragging,
        getDragStartOffsetFromHandleMiddleY,
    ] = useDragStartMouseOffsetFromHandleMiddleY();

    const PERIOD = 1;
    const RIGHT_SIDE_HORIZONTAL_DISTANCE_FROM_BAR = 40;
    const amountHandleWidth = isDraggingAmount ? 10000 : RIGHT_SIDE_HORIZONTAL_DISTANCE_FROM_BAR;
    const amountHandleHeight = isDraggingAmount ? 10000 : TEXT_HEIGHT;
    const amountHandleZIndex = 10;
    const barTop = principal + oldGrowth;
    const handleBottom = barTop + newGrowth / 2 - amountHandleHeight / 2;

    const onStartDraggingAmount = useCallback(
        (e) => {
            if (e.button !== 0) {
                return;
            }

            onStartDragging(e);
            setIsDraggingAmount(true);
            setIsDraggingAnything(true);
            e.stopPropagation();
        },
        [setIsDraggingAmount, setIsDraggingAnything, onStartDragging],
    );

    const onMouseMoveAmount = useCallback(
        (e) => {
            if (!isDraggingAmount) {
                return;
            }

            updateBarAmount(
                e.pageY - newGrowth / 2 - getDragStartOffsetFromHandleMiddleY(),
                PERIOD,
            );
            e.stopPropagation();
        },
        [isDraggingAmount, updateBarAmount, newGrowth, getDragStartOffsetFromHandleMiddleY],
    );

    useMouseUpListener(
        useCallback(
            (e) => {
                setIsDraggingAmount(false);
                setIsDraggingAnything(false);
                e.stopPropagation();
            },
            [setIsDraggingAmount, setIsDraggingAnything],
        ),
    );

    return (
        <div
            onMouseDown={onStartDraggingAmount}
            onMouseMove={onMouseMoveAmount}
            ref={handleRef}
            style={{
                position: 'absolute',
                bottom: handleBottom,
                right: -amountHandleWidth / 2 + RIGHT_SIDE_HORIZONTAL_DISTANCE_FROM_BAR,
                zIndex: amountHandleZIndex,
                width: amountHandleWidth,
                height: amountHandleHeight,

                cursor: 'row-resize',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: amountHandleWidth,
                    height: amountHandleHeight,
                }}
            >
                <Label
                    text={`${(growthRate * 100).toFixed(1)}%`}
                    color={color}
                    hasVerticalArrows={isHoveringProgression}
                />
            </div>
        </div>
    );
}

function LastBarLabels({
    principal,
    amount,
    lastBar,
    color,
    updateBarAmount,
    updateBarPrincipal,
    setIsDraggingAnything,
    isHoveringProgression,
}: {
    principal: number;
    amount: number;
    lastBar: BarT;
    color: string;
    updateBarAmount: (pageY: number, period: number) => void;
    updateBarPrincipal: (pageY: number, period: number) => void;
    setIsDraggingAnything: (isDragging: boolean) => void;
    isHoveringProgression: boolean;
}) {
    const textLeft = (lastBar.period + 1) * BAR_HORIZONTAL_INTERVAL - BAR_HORIZONTAL_INTERVAL + 7;

    const {oldGrowth, newGrowth, period} = lastBar;
    const [isDraggingAmount, setIsDraggingAmount] = useState<boolean>(false);
    const [isDraggingPrincipal, setIsDraggingPrincipal] = useState<boolean>(false);
    const [
        principalHandleRef,
        onStartDraggingPrincipalHandle,
        getDragStartOffsetFromPrincipalHandleMiddleY,
    ] = useDragStartMouseOffsetFromHandleMiddleY();
    const [
        amountHandleRef,
        onStartDraggingAmountHandle,
        getDragStartOffsetFromAmountHandleMiddleY,
    ] = useDragStartMouseOffsetFromHandleMiddleY();

    const onStartDraggingAmount = useCallback(
        (e) => {
            if (e.button !== 0) {
                return;
            }

            onStartDraggingAmountHandle(e);
            setIsDraggingAmount(true);
            setIsDraggingAnything(true);
            e.stopPropagation();
        },
        [setIsDraggingAmount, setIsDraggingAnything, onStartDraggingAmountHandle],
    );

    const onMouseMoveAmount = useCallback(
        (e) => {
            if (!isDraggingAmount) {
                return;
            }

            updateBarAmount(e.pageY - getDragStartOffsetFromAmountHandleMiddleY(), period);
            e.stopPropagation();
        },
        [isDraggingAmount, updateBarAmount, period, getDragStartOffsetFromAmountHandleMiddleY],
    );

    const onStartDraggingPrincipal = useCallback(
        (e) => {
            if (e.button !== 0) {
                return;
            }

            onStartDraggingPrincipalHandle(e);
            setIsDraggingPrincipal(true);
            setIsDraggingAnything(true);
            e.stopPropagation();
        },
        [setIsDraggingPrincipal, setIsDraggingAnything, onStartDraggingPrincipalHandle],
    );

    const onMouseMovePrincipal = useCallback(
        (e) => {
            if (!isDraggingPrincipal) {
                return;
            }

            updateBarPrincipal(e.pageY - getDragStartOffsetFromPrincipalHandleMiddleY(), period);
            e.stopPropagation();
        },
        [
            isDraggingPrincipal,
            updateBarPrincipal,
            period,
            getDragStartOffsetFromPrincipalHandleMiddleY,
        ],
    );

    useMouseUpListener(
        useCallback(
            (e) => {
                setIsDraggingAmount(false);
                setIsDraggingPrincipal(false);
                setIsDraggingAnything(false);
                e.stopPropagation();
            },
            [setIsDraggingAmount, setIsDraggingPrincipal, setIsDraggingAnything],
        ),
    );

    const LEFT_SIDE_HORIZONTAL_DISTANCE_FROM_BAR = 20;
    const amountHandleWidth = isDraggingAmount ? 10000 : LEFT_SIDE_HORIZONTAL_DISTANCE_FROM_BAR;
    const amountHandleHeight = isDraggingAmount ? 10000 : TEXT_HEIGHT;
    const principalHandleWidth = isDraggingPrincipal
        ? 10000
        : LEFT_SIDE_HORIZONTAL_DISTANCE_FROM_BAR;
    const principalHandleHeight = isDraggingPrincipal ? 10000 : TEXT_HEIGHT;

    return (
        <>
            <div
                onMouseDown={onStartDraggingAmount}
                onMouseMove={onMouseMoveAmount}
                ref={amountHandleRef}
                style={{
                    position: 'absolute',
                    bottom: principal + oldGrowth + newGrowth - amountHandleHeight / 2,
                    left: textLeft - amountHandleWidth / 2 + LEFT_SIDE_HORIZONTAL_DISTANCE_FROM_BAR,
                    zIndex: 10,
                    width: amountHandleWidth,
                    height: amountHandleHeight,
                    cursor: 'row-resize',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: amountHandleWidth,
                        height: amountHandleHeight,
                    }}
                >
                    <Label
                        text={Math.round(amount).toString()}
                        color={color}
                        hasVerticalArrows={isHoveringProgression}
                    />
                </div>
            </div>

            <div
                onMouseDown={onStartDraggingPrincipal}
                onMouseMove={onMouseMovePrincipal}
                ref={principalHandleRef}
                style={{
                    position: 'absolute',
                    bottom: principal - principalHandleHeight / 2,
                    left:
                        textLeft -
                        principalHandleWidth / 2 +
                        LEFT_SIDE_HORIZONTAL_DISTANCE_FROM_BAR,
                    zIndex: 10,
                    width: principalHandleWidth,
                    height: principalHandleHeight,
                    cursor: 'row-resize',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: principalHandleWidth,
                        height: principalHandleHeight,
                    }}
                >
                    <Label
                        text={Math.round(principal).toString()}
                        color={color}
                        hasVerticalArrows={isHoveringProgression}
                    />
                </div>
            </div>
        </>
    );
}

function PeriodLabel({
    periods,
    color,
    setIsDraggingAnything,
    updatePeriods,
    isHoveringProgression,
}: {
    periods: number;
    color: string;
    setIsDraggingAnything: (isDraggingAnything: boolean) => void;
    updatePeriods: (pageX: number) => void;
    isHoveringProgression: boolean;
}) {
    const [isDragging, setIsDragging] = useState<boolean>(false);

    const periodsString = periods.toString();
    const periodTextWidth = getTextWidth(periodsString);

    const TOP_VERTICAL_DISTANCE_FROM_BAR = 20;
    const handleWidth = isDragging ? 10000 : periodTextWidth;
    const handleHeight = isDragging ? 10000 : TEXT_HEIGHT;

    const onStartDragging = useCallback(
        (e) => {
            if (e.button !== 0) {
                return;
            }

            setIsDragging(true);
            setIsDraggingAnything(true);
            e.stopPropagation();
        },
        [setIsDragging, setIsDraggingAnything],
    );

    const onMouseMove = useCallback(
        (e) => {
            if (!isDragging) {
                return;
            }

            const mouseOffsetFromCenterX =
                e.pageX -
                e.currentTarget.getBoundingClientRect().x -
                window.scrollX -
                handleWidth / 2;

            if (mouseOffsetFromCenterX > BAR_HORIZONTAL_INTERVAL) {
                updatePeriods(1);
                return;
            }

            if (mouseOffsetFromCenterX < -BAR_HORIZONTAL_INTERVAL) {
                updatePeriods(-1);
                return;
            }

            e.stopPropagation();
        },
        [isDragging, updatePeriods, handleWidth],
    );

    useMouseUpListener(
        useCallback(
            (e) => {
                setIsDragging(false);
                setIsDraggingAnything(false);
                e.stopPropagation();
            },
            [setIsDragging, setIsDraggingAnything],
        ),
    );

    const handleLeft =
        (periods - 1) * BAR_HORIZONTAL_INTERVAL + BAR_HORIZONTAL_INTERVAL / 2 - handleWidth / 2;

    return (
        <div
            onMouseDown={onStartDragging}
            onMouseMove={onMouseMove}
            style={{
                position: 'absolute',
                bottom: -TOP_VERTICAL_DISTANCE_FROM_BAR - handleHeight / 2,
                left: handleLeft,
                zIndex: 10,
                width: handleWidth,
                height: handleHeight,
                cursor: 'col-resize',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: handleWidth,
                    height: handleHeight,
                }}
            >
                <Label
                    text={`${periodsString}`}
                    color={color}
                    hasHorizontalArrows={isHoveringProgression}
                />
            </div>
        </div>
    );
}

function Label({
    text,
    color,
    hasVerticalArrows,
    hasHorizontalArrows,
}: {
    text: string;
    color: string;
    hasVerticalArrows?: boolean;
    hasHorizontalArrows?: boolean;
}) {
    const arrowFontSize = 14;
    const arrowOpacity = 0.5;
    const arrowColor = COLOR_SETS[color].principal;

    return (
        <div
            style={{
                opacity: 0.4,
                color: COLOR_SETS[color].principal,
                fontFamily: 'courier',
                fontWeight: 'bold',
                fontSize: 20,
                whiteSpace: 'nowrap',
                userSelect: 'none',
                padding: 15,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            <div
                style={{
                    color: arrowColor,
                    fontSize: arrowFontSize,
                    lineHeight: 0.5,
                    opacity: arrowOpacity,
                }}
            >
                {hasVerticalArrows ? '▲' : ' '}
            </div>

            <div style={{display: 'flex', alignItems: 'center'}}>
                <div
                    style={{
                        color: arrowColor,
                        fontSize: arrowFontSize,
                        paddingRight: 3,
                        opacity: arrowOpacity,
                    }}
                >
                    {hasHorizontalArrows ? '◀' : ' '}
                </div>

                {text}

                <div
                    style={{
                        color: arrowColor,
                        fontSize: arrowFontSize,
                        paddingLeft: 3,
                        opacity: arrowOpacity,
                    }}
                >
                    {hasHorizontalArrows ? '▶' : ' '}
                </div>
            </div>

            <div
                style={{
                    color: arrowColor,
                    fontSize: arrowFontSize,
                    lineHeight: 0.5,
                    opacity: arrowOpacity,
                }}
            >
                {hasVerticalArrows ? '▼' : ' '}
            </div>
        </div>
    );
}

function AddPeriods({
    height,
    top,
    left,
    setIsDraggingAnything,
    updatePeriods,
}: {
    height: number;
    top: number;
    left: number;
    setIsDraggingAnything: (isDragging: boolean) => void;
    updatePeriods: (pageX: number) => void;
}) {
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [pageY, setPageY] = useState<number | null>(null);

    const handleWidth = isDragging ? 2000 : 6;
    const handleHeight = isDragging ? 2000 : height;

    const onStartDragging = useCallback(
        (e) => {
            if (e.button !== 0) {
                return;
            }

            setIsDragging(true);
            setIsDraggingAnything(true);
            e.stopPropagation();
        },
        [setIsDragging, setIsDraggingAnything],
    );

    const onMouseMove = useCallback(
        (e) => {
            setPageY(e.pageY);

            if (!isDragging) {
                return;
            }

            const mouseOffsetFromCenterX =
                e.pageX -
                e.currentTarget.getBoundingClientRect().x -
                window.scrollX -
                handleWidth / 2;

            if (mouseOffsetFromCenterX > BAR_HORIZONTAL_INTERVAL) {
                updatePeriods(1);
                return;
            }

            if (mouseOffsetFromCenterX < -BAR_HORIZONTAL_INTERVAL) {
                updatePeriods(-1);
                return;
            }

            e.stopPropagation();
        },
        [isDragging, updatePeriods, handleWidth],
    );

    useMouseUpListener(
        useCallback(
            (e) => {
                setIsDragging(false);
                setIsDraggingAnything(false);
                e.stopPropagation();
            },
            [setIsDragging, setIsDraggingAnything],
        ),
    );

    return (
        <div
            onMouseDown={onStartDragging}
            onMouseMove={onMouseMove}
            style={{
                position: 'absolute',
                top:
                    isDragging && pageY
                        ? pageY - top - handleHeight / 2
                        : -height + height / 2 - handleHeight / 2,
                left: left - handleWidth / 2,
                width: handleWidth,
                height: handleHeight,
                cursor: 'col-resize',
            }}
        ></div>
    );
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
function Bar({
    bar,
    colorSet,
    updateBarPrincipal,
    updateBarAmount,
    isDraggingProgression,
    setIsDraggingAnything,
}: {
    bar: BarT;
    colorSet: ColorSetT;
    updateBarAmount: (pageY: number, period: number) => void;
    updateBarPrincipal: (pageY: number) => void;
    isDraggingProgression: boolean;
    setIsDraggingAnything: (isDragging: boolean) => void;
}) {
    const [isDraggingAmount, setIsDraggingAmount] = useState<boolean>(false);
    const [isDraggingPrincipal, setIsDraggingPrincipal] = useState<boolean>(false);

    const {principal, amount, newGrowth, oldGrowth, period} = bar;

    const amountHandleWidth = isDraggingAmount ? 10000 : BAR_WIDTH;
    const amountHandleHeight = isDraggingAmount ? 500 : 5;

    const principalHandleWidth = isDraggingPrincipal ? 10000 : BAR_WIDTH;
    const principalHandleHeight = isDraggingPrincipal ? 500 : 5;

    const onStartDraggingAmount = useCallback(
        (e) => {
            if (e.button !== 0) {
                return;
            }

            setIsDraggingAmount(true);
            setIsDraggingAnything(true);
            e.stopPropagation();
        },
        [setIsDraggingAmount, setIsDraggingAnything],
    );

    const onMouseMoveAmount = useCallback(
        (e) => {
            if (!isDraggingAmount) {
                return;
            }

            updateBarAmount(e.pageY, period);
            e.stopPropagation();
        },
        [isDraggingAmount, updateBarAmount, period],
    );

    useMouseUpListener(
        useCallback(
            (e) => {
                setIsDraggingAmount(false);
                setIsDraggingAnything(false);
                e.stopPropagation();
            },
            [setIsDraggingAmount, setIsDraggingAnything],
        ),
    );

    const onStartDraggingPrincipal = useCallback(
        (e) => {
            if (e.button !== 0) {
                return;
            }

            setIsDraggingPrincipal(true);
            setIsDraggingAnything(true);
            e.stopPropagation();
        },
        [setIsDraggingPrincipal, setIsDraggingAnything],
    );

    const onMouseMovePrincipal = useCallback(
        (e) => {
            if (!isDraggingPrincipal) {
                return;
            }

            updateBarPrincipal(e.pageY);
            e.stopPropagation();
        },
        [isDraggingPrincipal, updateBarPrincipal],
    );

    useMouseUpListener(
        useCallback(
            (e) => {
                setIsDraggingPrincipal(false);
                setIsDraggingAnything(false);
                e.stopPropagation();
            },
            [setIsDraggingPrincipal, setIsDraggingAnything],
        ),
    );

    const shouldShowPrincipalHandle = !isDraggingProgression && !isDraggingAmount;
    const shouldShowAmountHandle = !isDraggingPrincipal;

    // Most of the time we favor the amount handle so you can extend it from
    // 0 when it overlaps principal, which will reveal the principle handle.
    // But if principal is 0, favor principal handle because trying to increase
    // amount with a principal of 0 is impossible.
    const shouldFavorAmountHandleWhenHandlesOverlapping = principal > 0;
    const amountHandleZIndex = shouldFavorAmountHandleWhenHandlesOverlapping ? 10 : 1;
    const principalHandleZIndex = shouldFavorAmountHandleWhenHandlesOverlapping ? 1 : 10;

    const opacity = 0.5;
    const index = period - 1;

    return (
        <>
            {shouldShowAmountHandle && (
                <div
                    onMouseDown={onStartDraggingAmount}
                    onMouseMove={onMouseMoveAmount}
                    style={{
                        position: 'absolute',
                        zIndex: amountHandleZIndex,
                        cursor: 'row-resize',
                        top: -amount - amountHandleHeight / 2,
                        left:
                            index * BAR_HORIZONTAL_INTERVAL + BAR_WIDTH / 2 - amountHandleWidth / 2,
                        width: amountHandleWidth,
                        height: amountHandleHeight,
                    }}
                ></div>
            )}

            {shouldShowPrincipalHandle && (
                <div
                    onMouseDown={onStartDraggingPrincipal}
                    onMouseMove={onMouseMovePrincipal}
                    style={{
                        position: 'absolute',
                        zIndex: principalHandleZIndex,
                        cursor: 'row-resize',
                        top: -principal - principalHandleHeight / 2,
                        left:
                            index * BAR_HORIZONTAL_INTERVAL +
                            BAR_WIDTH / 2 -
                            principalHandleWidth / 2,
                        width: principalHandleWidth,
                        height: principalHandleHeight,
                    }}
                ></div>
            )}

            <div
                style={{
                    position: 'absolute',
                    bottom: principal + oldGrowth,
                    left: index * BAR_HORIZONTAL_INTERVAL,
                    width: BAR_WIDTH,
                    height: newGrowth,
                    backgroundColor: colorSet.newGrowth,
                    borderTopLeftRadius: 5,
                    borderTopRightRadius: 5,
                    borderBottomLeftRadius: newGrowth === 0 ? 5 : 0,
                    borderBottomRightRadius: newGrowth === 0 ? 5 : 0,
                    opacity,
                }}
            ></div>

            <div
                style={{
                    position: 'absolute',
                    bottom: principal,
                    left: index * BAR_HORIZONTAL_INTERVAL,
                    width: BAR_WIDTH,
                    height: oldGrowth,
                    backgroundColor: colorSet.oldGrowth,
                    borderTopLeftRadius: oldGrowth < 5 ? 5 : 0,
                    borderTopRightRadius: oldGrowth < 5 ? 5 : 0,
                    borderBottomLeftRadius: oldGrowth === 0 ? 5 : 0,
                    borderBottomRightRadius: oldGrowth === 0 ? 5 : 0,
                    opacity,
                }}
            ></div>

            <div
                style={{
                    position: 'absolute',
                    bottom: 0,
                    left: index * BAR_HORIZONTAL_INTERVAL,
                    width: BAR_WIDTH,
                    height: principal,
                    backgroundColor: colorSet.principal,
                    borderTopLeftRadius: amount <= principal ? 5 : 0,
                    borderTopRightRadius: amount <= principal ? 5 : 0,
                    borderBottomLeftRadius: 5,
                    borderBottomRightRadius: 5,
                    opacity,
                }}
            ></div>
        </>
    );
}

export default App;
